import { useState, type ReactNode } from "react";

import { useFileOperations, useStartWorkspaceExport } from "../../api/fileOperations";
import { useAuth } from "../../auth/auth-context";
import { Button } from "../../ui/Button";
import { SettingsPage, SettingsSection } from "../../ui/SettingsSection";
import { Toggle } from "../../ui/Toggle";
import { FileOperationList } from "./FileOperationList";

/**
 * `/settings/export` (`docs/spec/export-import.md` §2, §4).
 *
 * Starting an export is a request that returns immediately; everything after it happens on
 * the queue, so the page is really a history list that happens to have a button on top. The
 * archive is emailed as well as listed, because a workspace export can outlive this tab.
 */
export function ExportScreen(): ReactNode {
  const { session } = useAuth();
  const isAdmin = session?.info.user.role === "admin";
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [includePrivate, setIncludePrivate] = useState(true);
  const operations = useFileOperations("export");
  const start = useStartWorkspaceExport();

  return (
    <SettingsPage
      title="Export"
      description="Download the workspace as a zip of markdown files, one directory per collection."
    >
      <SettingsSection
        title="New export"
        description={
          isAdmin
            ? "The archive is built in the background. You will be emailed when it is ready, and it is kept for 15 days."
            : "Only a workspace administrator can export the whole workspace. You can still export a single collection from its menu."
        }
      >
        <div className="space-y-3">
          <Toggle
            label="Include attachments"
            description="Write uploaded files into the archive beside the documents that use them."
            checked={includeAttachments}
            onChange={setIncludeAttachments}
            disabled={!isAdmin}
          />
          <Toggle
            label="Include private collections"
            description="Collections with no workspace-wide permission are exported too."
            checked={includePrivate}
            onChange={setIncludePrivate}
            disabled={!isAdmin}
          />
          <Button
            variant="primary"
            disabled={!isAdmin || start.isPending}
            onClick={() => {
              start.mutate({ includeAttachments, includePrivate });
            }}
          >
            {start.isPending ? "Starting…" : "Export workspace"}
          </Button>
          {start.isError && (
            <p role="alert" className="text-sm text-danger">
              {start.error.message}
            </p>
          )}
          {start.isSuccess && (
            <p role="status" className="text-sm text-muted">
              Export started. It will appear below when it is ready.
            </p>
          )}
        </div>
      </SettingsSection>

      <SettingsSection
        title="Recent exports"
        description="Exports are deleted 15 days after they finish."
      >
        <FileOperationList
          label="Exports"
          operations={operations.data ?? []}
          pending={operations.isPending}
          error={operations.error}
        />
      </SettingsSection>
    </SettingsPage>
  );
}
